import { useMutation, useQuery } from '@tanstack/react-query';
import { GenericApiResponse } from '..';
import { apiKeys } from '../apiKeys';

export enum Medium {
  'html' = 'html',
  'pdf' = 'pdf',
  'word' = 'word',
}

export enum Service {
  'tax_guide' = 'tax_guide',
}

export type ContentRequest = {
  directLoginId?: string;
  companyId: string;
  templateType: TemplateType;
  planId: string;
  countryId: string;
  stateId?: string;
  language?: string;
  ad?: 1 | null;
  medium: Medium;
  serviceName: Service;
  taxCalculationPopupList?: Array<Partial<TaxCalculation>> | null;
  taxAdvantage?: TaxAdvantageResp | null;
};

export type postContentRequest = {
  taxCalculationObject?: taxCalculationObject;
};

export type taxCalculationObject = {
  numberOfShares: string;
  vestingPrice: string;
  salePrice: string;
  salary: string;
};
export const LanguageEnabled = false;
export type LayoutResponse = {
  id: string;
  companyId: string;
  layout: {
    bannerTop: "<header><img href='/abc.jpg'/></header>";
    fonts?: '.ttf';
    bannerBottom?: `<p>abc</p>`;
    headerHtml?: `<header><img href="/abc.jpg'/></header>`;
    disclaimer?: DisclaimerResponse;
  };
  countries: CountryResponse[];
  plans: PlanResponse[];
  companyName?: string;
  enableDownloadPdf?: boolean;
  folderName?: string;
  languages: LanguageResponse[];
  userCountryCode: string;
};

export type CountryResponse = {
  id: string;
  name: string;
  eligiblePlans?: string[];
  states?: StateResponse[];
};

export type LanguageResponse = {
  id: string;
  value: string;
};

export type PlanResponse = {
  id: string;
  name: string;
  planShortId?: string;
};

export type StateResponse = {
  id: string;
  name: string;
};

export type DisclaimerResponse = {
  copyright: string;
  footer: string;
};

export type TaxGuideContentResp = {
  content: string;
  chartContents?: Array<ChartResponse>;
  taxCalculationPopupList?: Array<TaxCalculation>;
  taxAdvantage?: TaxAdvantageResp;
  isWordDownloadEnabled: boolean;
  isPdfEnabled: boolean;
};

export type TaxAdvantageResp = {
  displayToggle: boolean;
  approvedConditionsId: number;
};

export type TaxCalculation = {
  id:
    | 'noOfShares'
    | 'sharePriceOnVesting'
    | 'sharePriceOnSale'
    | 'sharePriceOnAward'
    | 'salary';
  name: string;
  currencyCode: string;
  value: string;
};

export type apiErrorResponse = {
  responseStatus: errorResponse;
};

export type ChartResponse = {
  divId: string;
  title: string;
  chartData: Array<ChartData>;
};
type ChartData = {
  name: string;
  value: string;
  bgColor: string;
};

type errorResponse = { status: '500'; message: 'Internal server error' };
type LayoutApiResponse = GenericApiResponse<LayoutResponse>;

export type TaxGuideResponse = GenericApiResponse<TaxGuideContentResp>;

export type TemplateType =
  | 'freestanding'
  | 'custom'
  | 'shareworks'
  | 'intranet'
  | 'adminPreview'
  | 'clientPreview';

export const useLayoutData = (
  userId: string,
  template: TemplateType,
  ad: 1 | null,
) => {
  return useQuery({
    queryKey: apiKeys.taxGuide(userId, template),
    queryFn: () => getBootstrapData(userId, template, ad),
  });
};

export const apiURL = process.env.REACT_APP_API_URL;

const getBootstrapData = (
  userId: string,
  template: TemplateType,
  ad: 1 | null,
): Promise<LayoutApiResponse> => {
  return fetch(
    '/gi/getTaxGuideLayout?directLoginId=' +
      `${userId}&templateType=${template}&ad=${ad}`,
  )
    .then(resp => resp.json())
    .then((data: LayoutResponse) => ({
      data,
      statusCode: 200,
      statusMessage: 'Successful',
    }));
};

export const useTaxGuideContent = (contentRequestObj: ContentRequest) => {
  return useQuery({
    queryKey: apiKeys.taxGuideContent(contentRequestObj),
    queryFn: () => getTaxGuideContent(contentRequestObj),
    enabled: !!contentRequestObj.countryId && !!contentRequestObj.planId,
    staleTime: 120000,
  });
};

export const useTaxGuidePDFService = (contentRequestObj: ContentRequest) => {
  return useQuery({
    queryKey: apiKeys.taxGuideContent(contentRequestObj),
    queryFn: () =>
      downloadTaxGuide(
        '/gi/downloadTaxGuide',
        contentRequestObj,
        'taxguide.pdf',
      ),
    enabled: !!contentRequestObj.countryId && !!contentRequestObj.planId,
  });
};

export const useDownloadTaxGuidePDF = (contentRequestObj: ContentRequest) => {
  return useMutation({
    mutationFn: () =>
      downloadTaxGuide(
        '/gi/downloadTaxGuide',
        contentRequestObj,
        'taxguide.pdf',
      ),
  });
};

export const useDownloadTaxGuideWord = (contentRequestObj: ContentRequest) => {
  return useMutation({
    mutationFn: () =>
      downloadTaxGuide('/gi/downloadWord', contentRequestObj, 'taxguide.docx'),
  });
};

const getTaxGuideContent = (
  contentRequestObj: ContentRequest,
): Promise<TaxGuideResponse> => {
  return fetch('/gi/postTaxGuideContent', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(contentRequestObj),
  })
    .then(resp => resp.json())
    .then((data: TaxGuideContentResp) => ({
      data,
      statusCode: 200,
      statusMessage: 'Successful',
    }));
};

export const downloadTaxGuide = (
  fetchedUrl: string,
  contentRequestObj: ContentRequest,
  defaultFilename: string,
): Promise<File | void> => {
  let resp = new Response();
  return fetch(fetchedUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(contentRequestObj),
  })
    .then(response => {
      if (response.status === 200) {
        resp = response; // Assign the response to 'resp'
        return response.blob();
      } else return null;
    })
    .then(blob => {
      if (!blob) {
        window.alert(
          'Error downloading PDF at the moment. Please try after sometime!',
        );
      } else {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const filenameMatch = resp.headers
          ?.get('content-disposition')
          ?.match(/filename="(.+)"/);
        const sanitizedFilename = filenameMatch
          ? filenameMatch[1].replace(/[^\w.-]/g, '_')
          : defaultFilename;
        a.download = sanitizedFilename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    });
};
